<template>
  <div
    id="group-map"
    class="bg-white rounded-lg shadow p-3 border-2"
    :class="[
      {'border-primary': isAvailable === undefined},
      {'border-secondary': isAvailable === true},
      {'border-red-500': isAvailable === false}
    ]"
  >
    <div class="flex flex-row items-center justify-between pb-3 gap-3">
      <p>
        👥 {{ message ?? "Disponibilité dans les pharmacies partenaires" }} -
        <span
          class="text-primary text-sm hover:underline hover:opacity-75 cursor-pointer"
          @click="router.push({name: 'disruption-logistics.healthcenter-groups'})"
        >
          ⚙️&nbsp;Gérer vos groupes
        </span>
      </p>
      <base-spinner
        v-if="loading"
        size="small"
      />
      <icon
        v-else
        :name="isAvailable ? 'mdi-check-bold' : 'mdi-close-thick'"
        :color="isAvailable ? '#65efca' : 'red'"
        :size="25"
      />
    </div>
    <Dialog v-model:open="modalOpen">
      <DialogTrigger class="w-full hover:opacity-75">
        <healthcenter-group-availability-distuption-map
          ref="mapRef"
          :loading="loading"
          :disabled="true"
          :info-content="infoContent"
          :open="infoWindowOpened"
          :unique-health-centers="uniqueHealthCenters"
          :grouping-group="groupingGroup"
          @toggle-info-window="toggleInfoWindow"
        />
      </DialogTrigger>
      <DialogContent class="min-w-[80%] max-h-auto p-10">
        <DialogTitle>
          👥 Disponibilité dans les pharmacies partenaires
        </DialogTitle>
        <healthcenter-group-availability-distuption-map
          ref="mapRef"
          :loading="loading"
          :info-content="infoContent"
          :open="infoWindowOpened"
          :grouping-group="groupingGroup"
          :unique-health-centers="uniqueHealthCenters"
          @toggle-info-window="toggleInfoWindow"
        />
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {
    HealthcenterGroupMember
} from "@/disruption-logistics/interfaces/availability-providers";
import {HealthcenterGroup} from "@/disruption-logistics/interfaces/healthcenter-groups";
import {Dialog, DialogContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import Icon from "@/core/components/icon/Icon.vue";
import HealthcenterGroupAvailabilityDistuptionMap
    from "@/disruption-logistics/components/maps/healthcenter-groups/HealthcenterGroupAvailabilityDistuptionMap.vue";
import {useRouter} from "vue-router";

const props = defineProps<{
    product: any;
    data?: HealthcenterGroup[] | null;
    isAvailable?: boolean | null,
    message?: string
}>();

const router = useRouter();
const mapRef = ref<InstanceType<typeof HealthcenterGroupAvailabilityDistuptionMap>>();
const infoWindowOpened = ref(false);
const modalOpen = ref(false);
const infoContent = ref<{
    healthcenter: HealthcenterGroupMember;
    groups: HealthcenterGroup[];
    position: { lat: number; lng: number };
}>();

const loading = computed(() => {
    return !props.data;
});

const groupingGroup = computed(() => {
    if (!props.data) {
        return;
    }

    return props.data.find(group => group.is_grouping);
});

const uniqueHealthCenters = computed((): HealthcenterGroupMember[] => {
    if (!props.data) {
        return [];
    }

    const reducedArray = props.data.flatMap(group => group.healthcenters)
        .reduce((acc, healthcenter) => {
            return {
                ...acc,
                [healthcenter.id]: healthcenter
            };
        }, {});

    return Object.values(reducedArray);
});

watch(() => uniqueHealthCenters.value, () => {
    if (!uniqueHealthCenters.value?.length) {
        return;
    }

    mapRef.value?.fitMapToGroups();
}, {deep: true});

watch(() => modalOpen.value, () => {
    if (modalOpen.value) {
        mapRef.value?.fitMapToGroups();
    }
});

/**
 * Toggle the info window.
 */
const toggleInfoWindow = (healthcenter: HealthcenterGroupMember): void => {
    const groups = props.data?.filter(group => group.healthcenters.some(h => h.id === healthcenter.id)) ?? [];

    infoContent.value = {
        healthcenter,
        groups,
        position: {
            lat: healthcenter.address.latitude as number,
            lng: healthcenter.address.longitude as number
        }
    };

    infoWindowOpened.value = true;
};
</script>

<style scoped>

</style>
